import { ApiRequestError, ConfirmDialog } from '@h2hspark/ui';
import * as React from 'react';

import { useCancellationPreview } from '../event-orders/api';

import { useCancelMyRegistration } from './api';
import {
  CANCEL_ACTION_LABEL,
  CANCEL_CONFIRM_ACTION_LABEL,
  CANCEL_CONFIRM_TITLE,
  CANCEL_KEEP_LABEL,
  cancellationConsequence,
  whatCancellingMeans,
} from './constants';

/**
 * **Cancelling a registration** (`MEM-023`, `MEM-013` AC-6), as the quiet control under the status
 * pill in the event header card.
 *
 * Sumit, reviewing #528: *"I think we can get rid of the You're going row"*, with cancelling moved
 * *"under the green going badge"*. So the confirmed panel is gone and this is what is left of it —
 * a text link rather than a button, because on a page a member opens to read about an evening they
 * are going to, cancelling is the one thing that should not look like an invitation.
 *
 * **The flow itself is unchanged**, and that is the point of extracting it rather than rewriting
 * it: the refund preview is read first, the control is withheld until the server has said what
 * comes back (`MEM-023` AC-8 — a member who could confirm before reading the terms would be told
 * the deal after taking it), and the confirmation dialog carries the consequence (`MEM-013` AC-6)
 * and the money sentence in the server's own words.
 *
 * Nothing is drawn where there is no registration to cancel: the caller only renders it for a
 * member who holds one.
 */
export function CancelRegistrationControl({
  eventId,
  eventName,
}: {
  readonly eventId: string;
  readonly eventName: string;
}): React.ReactElement {
  const cancel = useCancelMyRegistration(eventId);
  const preview = useCancellationPreview(eventId, { enabled: true });
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);
  const failure = cancel.error ?? preview.error ?? null;
  /**
   * The cancel control waits for the preview, and is withheld if it cannot be read.
   *
   * Disabled rather than absent while the answer is on its way, because the control's existence is
   * not in question — only the sentence that has to be read before it is used. Where the preview
   * *failed* the failure is shown beside it and the control stays disabled: a money surface does
   * not offer a destructive act it cannot describe.
   */
  const cancellationMayBeOffered = preview.isSuccess;

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={cancel.isPending || !cancellationMayBeOffered}
        onClick={() => {
          setConfirmingCancel(true);
        }}
        className="text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-destructive disabled:no-underline disabled:opacity-50"
      >
        {CANCEL_ACTION_LABEL}
      </button>

      {failure === null ? null : <RefusalNotice error={failure} />}

      {/*
        `MEM-013` AC-6: cancelling removes the member's access to this event's meet list and its
        matches, **and the confirmation says so.** A consequence rather than a rule, which is why
        it has to be stated out loud — nothing else in the flow mentions it, and a member who finds
        out afterwards has already lost the plan they made.

        It is also why cancelling is not a single tap. The story calls this "a reason not to cancel
        casually", and a control that acts on the first tap cannot carry a reason.

        `MEM-023` AC-8: the money joins the consequence, in the server's own sentence — what comes
        back, and that the service fee does not — so both are read in the dialog's description, in
        reading order before the confirm control. `whatCancellingMeans` joins the two served
        sentences and composes nothing.
      */}
      <ConfirmDialog
        open={confirmingCancel}
        title={CANCEL_CONFIRM_TITLE}
        description={whatCancellingMeans(
          cancellationConsequence(eventName),
          preview.data?.preview ?? null,
        )}
        confirmLabel={CANCEL_CONFIRM_ACTION_LABEL}
        cancelLabel={CANCEL_KEEP_LABEL}
        destructive
        busy={cancel.isPending}
        onConfirm={() => {
          setConfirmingCancel(false);
          cancel.mutate();
        }}
        onOpenChange={setConfirmingCancel}
      />
    </div>
  );
}

/**
 * The server's own sentence, shown as written.
 *
 * An `ApiRequestError` carries the API's message; anything else is a transport failure the member
 * cannot act on, so it gets a generic line rather than a stack-shaped one.
 */
function RefusalNotice({ error }: { readonly error: Error }): React.ReactElement {
  const message =
    error instanceof ApiRequestError && error.message.length > 0
      ? error.message
      : 'Something went wrong on our side. Try again in a moment.';
  return (
    <p role="status" className="max-w-[220px] text-right text-xs text-destructive">
      {message}
    </p>
  );
}
